import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch {
      localStorage.removeItem('cop_token');
      sessionStorage.removeItem('cop_session_token');
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('cop_token');
    if (token) {
      refreshUser();
    } else {
      setLoading(false);
    }
  }, [refreshUser]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('cop_token', data.token);
    sessionStorage.setItem('cop_session_token', data.session_token);
    setUser(data.user);
    return data;
  };

  const signup = async (email, password, confirmPassword) => {
    const fp = `${navigator.userAgent}_${window.screen.width}x${window.screen.height}_${new Date().getTimezoneOffset()}`;
    const { data } = await api.post('/auth/signup', {
      email,
      password,
      confirm_password: confirmPassword,
      fingerprint: fp,
    });
    return data;
  };

  const logout = () => {
    localStorage.removeItem('cop_token');
    sessionStorage.removeItem('cop_session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
